import { useEffect, useState, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "@/lib/api";
import { MapPin, Navigation, Search, ExternalLink, Footprints, Car } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Seo } from "@/components/Seo";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const LONDON_CENTER = [51.5117, -0.1275];

function distanceMiles(lat1, lng1, lat2, lng2) {
  const R = 3958.8;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function directionsUrl(venue, fromText, fromCoords) {
  const destination = encodeURIComponent(`${venue.name}, London`);
  const origin = fromCoords
    ? `${fromCoords.lat},${fromCoords.lng}`
    : encodeURIComponent(fromText || "");
  return `https://www.google.com/maps/dir/?api=1&destination=${destination}${origin ? `&origin=${origin}` : ""}`;
}

export default function Theatres() {
    return (
      <>
        <Seo
          title="London theatre map and directions"
          description="Find every West End theatre on a map, see the venues nearest to you, and get driving times and directions."
/>
        <PageHero
          offset={3}
          overline="Find your way"
          title="Theatre Map"
          callout={{
            icon: MapPin,
            lead: "Plan your journey.",
            text: "Search a venue, find it on the map, and enter your address to see the nearest theatres and driving times. For public transport, the directions link opens Google Maps.",
          }}
        />
        <TheatresContent />
      </>
    );
  }
  
  function TheatresContent() {
  const [venues, setVenues] = useState([]);
  const [search, setSearch] = useState("");
  const [userLocation, setUserLocation] = useState(null);
  const [fromText, setFromText] = useState("");
  const [locError, setLocError] = useState("");
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [routes, setRoutes] = useState({ driving: null });
  const [routeLoading, setRouteLoading] = useState(false);
  const markerRefs = useRef({});
  const mapRef = useRef(null);

  useEffect(() => {
    api.get("/venues").then((r) => setVenues(r.data)).catch(() => setVenues([]));
  }, []);

  const useMyLocation = () => {
    setLocError("");
    if (!navigator.geolocation) {
      setLocError("Your browser doesn't support location.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setFromText("");
      },
      () => setLocError("Couldn't get your location. Check your browser's location permission.")
    );
  };

  const openMarker = async (venue) => {
    const marker = markerRefs.current[venue.name];
    if (marker) {
      marker.openPopup();
      if (mapRef.current) mapRef.current.flyTo([venue.lat, venue.lng], 15, { duration: 0.6 });
    }
    setSelectedVenue(venue);

    if (userLocation) {
        setRouteLoading(true);
        setRoutes({ driving: null });
        try {
          const driveRes = await api.get("/route", { params: { from_lat: userLocation.lat, from_lng: userLocation.lng, to_lat: venue.lat, to_lng: venue.lng } });
          setRoutes({ driving: driveRes.data });
        } catch {
          setRoutes({ driving: null });
        } finally {
          setRouteLoading(false);
        }
      }
  }

  const filtered = venues.filter((v) => v.name.toLowerCase().includes(search.toLowerCase()));

  const sorted = userLocation
    ? [...filtered].sort(
        (a, b) =>
          distanceMiles(userLocation.lat, userLocation.lng, a.lat, a.lng) -
          distanceMiles(userLocation.lat, userLocation.lng, b.lat, b.lng)
      )
    : filtered;

    return (
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
      <div className="mb-6 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search theatres…"
            className="w-full rounded-full border border-input bg-card py-2.5 pl-10 pr-4 text-sm outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (!fromText.trim()) return;
            try {
              const { data } = await api.get("/geocode", { params: { q: fromText } });
              setUserLocation(data);
            } catch {
              setLocError("Couldn't find that address. Try being more specific.");
            }
          }}
          className="flex flex-1 min-w-[200px] gap-2"
        >
          <input
            value={fromText}
            onChange={(e) => setFromText(e.target.value)}
            placeholder="Type a starting address, then press Enter…"
            className="flex-1 rounded-full border border-input bg-card px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring"
          />
        </form>
        <button
          onClick={useMyLocation}
          className="flex items-center gap-2 rounded-full bg-[#9E1B28] px-5 py-2.5 text-sm font-medium text-amber-50 hover:bg-[#B22232]"
        >
          <Navigation className="h-4 w-4" /> Use my location
        </button>
      </div>
      {locError && <p className="mb-4 text-sm text-red-400">{locError}</p>}

      {selectedVenue && userLocation && (
        <div className="mb-6 rounded-2xl border border-amber-900/20 bg-card p-5">
          <h3 className="font-serif text-lg font-semibold">
            Getting to {selectedVenue.name}
          </h3>
          {routeLoading && <p className="mt-2 text-sm text-muted-foreground">Calculating route…</p>}
          {!routeLoading && routes.driving && (
  <div className="mt-3 flex flex-wrap gap-6">
    <div className="flex items-center gap-2">
      <Car className="h-5 w-5 text-[#C89B3C]" />
      <span>{routes.driving.duration_minutes} min drive ({routes.driving.distance_miles} mi)</span>
    </div>
  </div>
)}
<p className="mt-3 text-xs text-muted-foreground">
  For public transport directions, use "Get directions" below to open Google Maps.
</p>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="overflow-hidden rounded-2xl border border-border lg:col-span-2" style={{ height: "500px" }}>
          <MapContainer center={LONDON_CENTER} zoom={13} style={{ height: "100%", width: "100%" }} ref={mapRef}>
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
            {filtered.map((v) => (
              <Marker key={v.name} position={[v.lat, v.lng]} ref={(ref) => { if (ref) markerRefs.current[v.name] = ref; }} eventHandlers={{ click: () => openMarker(v) }}>
                <Popup>
                  <div>
                    <p className="font-medium">{v.name}</p>
                    <a
                      href={directionsUrl(v, fromText, userLocation)}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-1 inline-block text-sm text-blue-600 underline"
                    >
                      Get directions
                    </a>
                  </div>
                </Popup>
              </Marker>
            ))}
            {userLocation && (
              <Marker
                position={[userLocation.lat, userLocation.lng]}
                icon={L.divIcon({ className: "", html: '<div style="background:#C89B3C;width:16px;height:16px;border-radius:50%;border:2px solid white"></div>' })}
              >
                <Popup>You are here</Popup>
              </Marker>
            )}
          </MapContainer>
        </div>

        <div className="rounded-2xl border border-border bg-card p-5" style={{ maxHeight: "500px", overflowY: "auto" }}>
          <h2 className="mb-3 font-serif text-xl font-semibold">
            {userLocation ? "Nearest to you" : `${filtered.length} theatre${filtered.length !== 1 ? "s" : ""}`}
          </h2>
          <div className="space-y-2">
            {sorted.map((v) => (
              <div key={v.name} className="rounded-lg bg-secondary/50 px-3 py-2 text-sm">
                <button onClick={() => openMarker(v)} className="flex w-full items-center justify-between text-left">
                  <span className="flex items-center gap-2">
                    <MapPin className="h-3.5 w-3.5 text-[#C89B3C]" /> {v.name}
                  </span>
                  {userLocation && (
                    <span className="text-muted-foreground">
                      {distanceMiles(userLocation.lat, userLocation.lng, v.lat, v.lng).toFixed(1)} mi
                    </span>
                  )}
                </button>
                <a
                  href={directionsUrl(v, fromText, userLocation)}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-1.5 flex items-center gap-1 text-xs text-[#C89B3C] hover:underline"
                >
                  <ExternalLink className="h-3 w-3" /> Get directions
                </a>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}